// Row contracts and jsonb shapes of DE configuration (DC-01; 02 §DE configuration; ADR-0018,
// ADR-0019, ADR-0020 as widened by the founder on 2026-10-20). Same conventions as `rows.ts`:
// keys are column names, nullability is the column's, enums are the CHECK's; bound to their
// tables in `contract-bindings.ts`. The camelCase shapes the handlers validate with live in
// `packages/domain-agents/src/schemas.ts`; the jsonb shapes here are their stored snake_case
// form (`de-config.test.ts` checks the enums agree).
import { z } from "zod";
import { ActorShortColumns } from "./actor";
import {
  CliLoginLocation,
  DeployErrorCode,
  DeployKind,
  DeployPath,
  DeployReconcileState,
  DeployState,
  DriftAuditState,
  DriftAuditTrigger,
  EnforcerKind,
  ModelCredentialKind,
  ModelCredentialScope,
  ModelCredentialState,
  ModelPath,
  ModelTier,
  RunDecisionResult,
  RunEscalationKind,
  TemplateEditSource,
  TemplateEditState,
  TemplateEvalKind,
  TemplateEvalResult,
  TemplateVersionSource,
  TemplateVersionState,
  VerificationState,
} from "./enums";
import {
  BoundedJson,
  boundedRecord,
  LIMITS,
  MicrosAmount,
  Timestamp,
  text,
  Uuid,
} from "./primitives";
import { ReportingDefaults } from "./reach-rows";
import {
  RoleAccessDefaults,
  RoleCadenceRow,
  RoleKpiRow,
  RoleProbation,
  RoleRequiredConnection,
} from "./template-rows";

const actor = ActorShortColumns.shape;
const stamps = { created_at: Timestamp, updated_at: Timestamp };
export const Sha256Hex = z
  .string()
  .length(64)
  .regex(/^[0-9a-f]{64}$/, "64 lowercase hex characters");
const Json = z.record(z.string(), z.unknown());

// jsonb shapes ----------------------------------------------------------------------------------

/**
 * A pointer, never prose (01 "Decision log"; ADR-0019 §1): what a run produced or the evidence
 * of its verdict. An `object` id is a key under the run's own prefix `runs/{company_id}/{run_id}/`.
 */
export const PointerRef = z
  .strictObject({
    // TASK-FILES: `attachment` — a run's output may be a task file it uploaded.
    kind: z.enum([
      "run_step",
      "object",
      "task",
      "message",
      "page",
      "external_action",
      "attachment",
    ]),
    id: z.string().min(1).max(512),
  })
  .meta({ id: "PointerRef" });
export type PointerRef = z.infer<typeof PointerRef>;

/** A run in one company (read per company through the agency grant, never across companies). */
export const RunRef = z.strictObject({ company_id: Uuid, run_id: Uuid }).meta({ id: "DeRunRef" });
export type RunRef = z.infer<typeof RunRef>;

/** `{company_id, run_id, run_decision_id?}`: the past mistake an eval or enforcer is proven on. */
export const MistakeRef = z
  .strictObject({ company_id: Uuid, run_id: Uuid, run_decision_id: Uuid.optional() })
  .meta({ id: "MistakeRef" });
export type MistakeRef = z.infer<typeof MistakeRef>;

/**
 * One role of the role-to-model map (01): a gateway model alias and the path order. One path is
 * a per-run choice; `["subscription", "gateway"]` is the only fallback order and moves a run
 * only when its owner pre-approved the fallback (ADR-0020 widened; never silent).
 */
export const ModelMapEntry = z
  .strictObject({
    model: z.string().min(1).max(LIMITS.key),
    paths: z
      .array(ModelPath)
      .min(1)
      .max(2)
      .refine((p) => p.length === 1 || (p[0] === "subscription" && p[1] === "gateway"), {
        message: 'a fallback order is only ["subscription", "gateway"]',
      }),
  })
  .meta({ id: "ModelMapEntry" });
/** 01 "Role-to-model map": pipeline role (`draft`, `review`, `verify`, …) → model entry. */
export const RoleModelMap = boundedRecord(
  ModelMapEntry,
  LIMITS.recordKeys,
  z
    .string()
    .max(LIMITS.key)
    .regex(/^[a-z][a-z0-9_]*$/),
).meta({ id: "RoleModelMap" });
export type RoleModelMap = z.infer<typeof RoleModelMap>;

/** Who did something on a control-plane row: a person (cp user) or a run in a company. */
export const ConfigActorRef = z
  .discriminatedUnion("kind", [
    z.strictObject({ kind: z.literal("user"), user_id: Uuid }),
    z.strictObject({ kind: z.literal("run"), company_id: Uuid, run_id: Uuid }),
  ])
  .meta({ id: "ConfigActorRef" });
export type ConfigActorRef = z.infer<typeof ConfigActorRef>;

/**
 * `deploy.staged_by`: the Deployment engineer's run (ADR-0018 §6), an agency operator, or for
 * `self_serve` the company owner or admin who staged it (a membership of the target company).
 */
export const StagedBy = z
  .discriminatedUnion("kind", [
    z.strictObject({ kind: z.literal("run"), company_id: Uuid, run_id: Uuid }),
    z.strictObject({ kind: z.literal("user"), user_id: Uuid }),
    z.strictObject({ kind: z.literal("member"), company_id: Uuid, membership_id: Uuid }),
  ])
  .meta({ id: "StagedBy" });
export type StagedBy = z.infer<typeof StagedBy>;

/** ADR-0018 §3 intake response / §4 callback: where the panel put the DE. */
export const PanelRef = z
  .strictObject({
    instance_id: z.string().min(1).max(128),
    profile_name: z
      .string()
      .max(64)
      // Phase 2 increment 3: `cos-<company>-<role>-<name>-<id>` (the panel's rule,
      // `^cos-[a-z0-9][a-z0-9_-]{0,59}$`, up to 64), the legacy `cos-<hex>`, and the AGENT-POLISH
      // readable names stored before (lowercase [a-z0-9-], ≤ 63). Widened to the panel's `_`
      // and edge characters so any name the panel accepted is recorded as it answered it.
      .regex(/^[a-z0-9][a-z0-9_-]{0,63}$/),
    node_id: z.string().min(1).max(128),
    api_base_url: z.url().max(2048),
  })
  .meta({ id: "PanelRef" });
export type PanelRef = z.infer<typeof PanelRef>;

/** ADR-0018 §4 `error`: a code from the contract and a message (never a secret). */
export const DeployError = z
  .strictObject({ code: DeployErrorCode, message: z.string().max(LIMITS.text) })
  .meta({ id: "DeployError" });
export type DeployError = z.infer<typeof DeployError>;

/** `configuration_bundle.inline_files` (ADR-0018 §3 `files` form, ≤ 1 MiB, before a bucket). */
export const BundleFile = z
  .strictObject({ path: z.string().min(1).max(256), content: z.string() })
  .meta({ id: "BundleFile" });

/** `drift_audit.results`: eval key → pass | fail. */
export const DriftResults = z.record(z.string().min(1).max(LIMITS.key), z.enum(["pass", "fail"]));

// catalog (control plane) -----------------------------------------------------------------------

export const AgentTemplateVersion = z
  .object({
    id: Uuid,
    template_id: Uuid,
    version: z.int().min(1),
    state: TemplateVersionState,
    source: TemplateVersionSource,
    supersedes_version_id: Uuid.nullable(),
    skill_index_ref: z.string().nullable(),
    skill_index_tokens: z.int().min(0).nullable(),
    skill_index_token_cap: z.int().min(1),
    verification_skill_pack_id: Uuid.nullable(),
    role_model_map: RoleModelMap,
    standing_orders_text: z.string(),
    tool_grants: Json,
    runtime_config: Json,
    /** REACH-S1: the Reach a DE deployed from this version starts with (`{}`: the platform's). */
    reporting_defaults: ReportingDefaults,
    content_hash: Sha256Hex.nullable(),
    validation_report: Json.nullable(),
    validated_at: Timestamp.nullable(),
    published_at: Timestamp.nullable(),
    deprecated_at: Timestamp.nullable(),
    created_by_user_id: Uuid.nullable(),
    changelog: z.string().nullable(),
    // TEMPLATES-T2: a role template's defaults (all null for any other version)
    required_connections: z.array(RoleRequiredConnection).nullable(),
    access_defaults: RoleAccessDefaults.nullable(),
    kpis: z.array(RoleKpiRow).nullable(),
    cadence: z.array(RoleCadenceRow).nullable(),
    model_tier: ModelTier.nullable(),
    budget_default_minor: z.int().min(0).nullable(),
    probation: RoleProbation.nullable(),
    ...stamps,
  })
  .meta({ id: "AgentTemplateVersion" });
export type AgentTemplateVersion = z.infer<typeof AgentTemplateVersion>;

export const TemplateEval = z
  .object({
    id: Uuid,
    template_version_id: Uuid,
    key: z.string(),
    kind: TemplateEvalKind,
    spec_ref: z.string(),
    proves_mistake_ref: MistakeRef.nullable(),
    fails_on_version_id: Uuid.nullable(),
    passes_on_version_id: Uuid.nullable(),
    last_result: TemplateEvalResult.nullable(),
    last_run_at: Timestamp.nullable(),
    retired_at: Timestamp.nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "TemplateEval" });
export type TemplateEval = z.infer<typeof TemplateEval>;

export const TemplateRule = z
  .object({
    id: Uuid,
    template_version_id: Uuid,
    key: z.string(),
    rule_text: z.string(),
    enforcer_kind: EnforcerKind,
    enforcer_ref: z.string(),
    proven_against_ref: MistakeRef,
    proven_at: Timestamp.nullable(),
    reason_if_written: z.string().nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "TemplateRule" });
export type TemplateRule = z.infer<typeof TemplateRule>;

export const TemplateEditProposal = z
  .object({
    id: Uuid,
    template_id: Uuid,
    based_on_version_id: Uuid,
    source: TemplateEditSource,
    run_refs: z.array(RunRef),
    diff_ref: z.string(),
    eval_id: Uuid,
    state: TemplateEditState,
    accepted_version_id: Uuid.nullable(),
    proposed_by: ConfigActorRef,
    decided_by_user_id: Uuid.nullable(),
    decided_at: Timestamp.nullable(),
    reason: z.string().nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "TemplateEditProposal" });
export type TemplateEditProposal = z.infer<typeof TemplateEditProposal>;

// control plane ---------------------------------------------------------------------------------

export const ConfigurationBundle = z
  .object({
    id: Uuid,
    template_version_id: Uuid,
    company_id: Uuid,
    de_template_override_id: Uuid.nullable(),
    override_hash: Sha256Hex.nullable(),
    bundle_sha: Sha256Hex,
    object_key: z.string().nullable(),
    skill_index_tokens: z.int().min(0),
    verification_state: VerificationState,
    validation_report: Json,
    validated_at: Timestamp,
    built_by: ConfigActorRef,
    created_at: Timestamp,
  })
  .meta({ id: "ConfigurationBundle" });
export type ConfigurationBundle = z.infer<typeof ConfigurationBundle>;

export const Deploy = z
  .object({
    id: Uuid,
    path: DeployPath,
    agency_id: Uuid.nullable(),
    target_company_id: Uuid,
    de_membership_id: Uuid,
    template_id: Uuid,
    template_version_id: Uuid,
    /** Null while a version with no verification skill waits for its override (DC-05). */
    bundle_id: Uuid.nullable(),
    kind: DeployKind,
    state: DeployState,
    staged_by: StagedBy,
    approved_by_user_id: Uuid.nullable(),
    approved_by_membership_id: Uuid.nullable(),
    approved_at: Timestamp.nullable(),
    rejected_reason: z.string().nullable(),
    handed_off_at: Timestamp.nullable(),
    handoff_attempts: z.int().min(0),
    panel_ref: PanelRef.nullable(),
    confirmed_at: Timestamp.nullable(),
    failed_at: Timestamp.nullable(),
    error: DeployError.nullable(),
    previous_deploy_id: Uuid.nullable(),
    /**
     * Phase 2 increment 4: a closed deploy whose panel outcome is not certain, and until when it
     * is polled (null: none).
     */
    reconcile_state: DeployReconcileState.nullable(),
    reconcile_until: Timestamp.nullable(),
    created_at: Timestamp,
    updated_at: Timestamp,
  })
  .meta({ id: "Deploy" });
export type Deploy = z.infer<typeof Deploy>;

export const VerificationOverride = z
  .object({
    id: Uuid,
    deploy_id: Uuid,
    bundle_id: Uuid,
    target_company_id: Uuid,
    operator_user_id: Uuid,
    reason: z.string(),
    pilot_until: Timestamp,
    created_at: Timestamp,
  })
  .meta({ id: "VerificationOverride" });
export type VerificationOverride = z.infer<typeof VerificationOverride>;

/**
 * A person's or a company's model credential (ADR-0020 widened, founder 2026-10-20). The sealed
 * API key never leaves the server (omitted here); a `cli_subscription` row has no secret at all,
 * only where the vendor CLI's login lives.
 */
export const ModelCredential = z
  .object({
    id: Uuid,
    scope: ModelCredentialScope,
    kind: ModelCredentialKind,
    company_id: Uuid,
    owner_user_id: Uuid.nullable(),
    provider: z.string(),
    label: z.string(),
    plan: z.string().nullable(),
    /** `api_key`: its last four characters, the only part ever shown (BYO-01). */
    key_last4: z.string().nullable(),
    login_location: CliLoginLocation.nullable(),
    runtime_ref: z.string().nullable(),
    fallback_to_gateway: z.boolean(),
    state: ModelCredentialState,
    last_verified_at: Timestamp.nullable(),
    created_by_user_id: Uuid,
    revoked_at: Timestamp.nullable(),
    revoked_by_user_id: Uuid.nullable(),
    ...stamps,
  })
  .meta({ id: "ModelCredential" });
export type ModelCredential = z.infer<typeof ModelCredential>;

export const SubscriptionAttestation = z
  .object({
    id: Uuid,
    credential_id: Uuid,
    provider: z.string(),
    plan: z.string().nullable(),
    terms_ref: z.string(),
    terms_version: z.string().nullable(),
    plan_is_own: z.boolean(),
    own_work_only: z.boolean(),
    allows_paid_client_work: z.boolean(),
    attested_by_user_id: Uuid,
    attested_at: Timestamp,
    revoked_at: Timestamp.nullable(),
    revoke_reason: z.string().nullable(),
  })
  .meta({ id: "SubscriptionAttestation" });
export type SubscriptionAttestation = z.infer<typeof SubscriptionAttestation>;

/**
 * Admission policy for person and company credentials (founder decisions pending, OPEN-QUESTIONS
 * 46–50). Stored as JSON in `cp.platform_setting` under `MODEL_CREDENTIAL_POLICY_SETTING`, which
 * only the schema owner writes; a missing row is these defaults: everything off, so admission
 * refuses every person/company credential until the founder turns a path on.
 */
export const ModelCredentialPolicy = z
  .strictObject({
    /** People may add their own provider API keys (gateway upstream, their own crew). */
    person_api_keys: z.boolean().default(false),
    /** Company owners/admins may add company API keys for the company's agents. */
    company_api_keys: z.boolean().default(false),
    /** People may link their own plan login in the vendor's unmodified CLI (own crew only). */
    person_cli_subscriptions: z.boolean().default(false),
    /** A person's API key may power company work (DE runs), not only their own crew. */
    person_key_for_company_work: z.boolean().default(false),
    /** A subscription run may fall back to the gateway when its owner pre-approved it. */
    owner_preapproved_fallback: z.boolean().default(false),
    /** OpenAI "Sign in with ChatGPT" as a `cli_subscription` provider (OPEN-QUESTIONS 49). */
    openai_sign_in_with_chatgpt: z.boolean().default(false),
    /** ADR-0020's platform-operator subscription for the Deployment engineer (OPEN-QUESTIONS 46). */
    deployment_engineer_subscription: z.boolean().default(false),
  })
  .meta({ id: "ModelCredentialPolicy" });
export type ModelCredentialPolicy = z.infer<typeof ModelCredentialPolicy>;
export const MODEL_CREDENTIAL_POLICY_SETTING = "model_credential_policy";

// cell ------------------------------------------------------------------------------------------

export const DeTemplateOverride = z
  .object({
    company_id: Uuid,
    id: Uuid,
    de_membership_id: Uuid,
    /** DE-WIZARD: null = the DE's own, before any template is deployed or staged. */
    template_id: Uuid.nullable(),
    standing_orders_text: z.string().nullable(),
    role_model_map: RoleModelMap.nullable(),
    version: z.int().min(1),
    ...actor,
    ...stamps,
  })
  .meta({ id: "DeTemplateOverride" });
export type DeTemplateOverride = z.infer<typeof DeTemplateOverride>;

/** DE-WIZARD: how often a DE reports a KPI itself (a placeholder until reporting obligations). */
export const DeReportingDuty = z.enum(["daily", "weekly", "monthly"]);
/** DE-WIZARD: one KPI of the DE's seat that the DE reports itself (`de_job.reporting`). */
export const DeJobReporting = z
  .strictObject({ metric_def_id: Uuid, duty: DeReportingDuty })
  .meta({ id: "DeJobReporting" });
export type DeJobReporting = z.infer<typeof DeJobReporting>;

export const DeJob = z
  .object({
    company_id: Uuid,
    de_membership_id: Uuid,
    path: DeployPath,
    submitted_at: Timestamp.nullable(),
    home_channel_id: Uuid.nullable(),
    reporting: z.array(DeJobReporting).max(LIMITS.list),
    version: z.int().min(1),
    ...actor,
    ...stamps,
  })
  .meta({ id: "DeJob" });
export type DeJob = z.infer<typeof DeJob>;

export const RunDecision = z
  .object({
    company_id: Uuid,
    id: Uuid,
    run_id: Uuid,
    seq: z.int().min(1),
    decided_at: Timestamp,
    decision: z.string(),
    why: z.string(),
    evidence_ref: PointerRef.nullable(),
    result: RunDecisionResult,
    escalation_kind: RunEscalationKind.nullable(),
    ...actor,
    created_at: Timestamp,
  })
  .meta({ id: "RunDecision" });
export type RunDecision = z.infer<typeof RunDecision>;

export const DriftAudit = z
  .object({
    company_id: Uuid,
    id: Uuid,
    de_membership_id: Uuid,
    template_version_id: Uuid,
    trigger: DriftAuditTrigger,
    scheduled_for: Timestamp,
    started_at: Timestamp.nullable(),
    ended_at: Timestamp.nullable(),
    state: DriftAuditState,
    results: DriftResults,
    task_id: Uuid.nullable(),
    cost_micros: MicrosAmount,
    ...actor,
    ...stamps,
  })
  .meta({ id: "DriftAudit" });
export type DriftAudit = z.infer<typeof DriftAudit>;

/** A short single line used by several bodies (≤ 280 chars, ADR-0019 §1 `note`). */
export const ShortNote = text(280);

/** Bounded open JSON for request bodies (`tool_grants`, `runtime_config`). */
export const ConfigJson = BoundedJson;
